import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { isHtmlContent, stripHtmlToPlainText } from '../../utils/richText';

const AUTO_ADVANCE_MS = 7000;

export default function FeaturedCarousel({ sessions = [] }) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = sessions.length;

  const goTo = useCallback((next) => {
    if (count === 0) return;
    setIndex(((next % count) + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return undefined;
    const id = window.setInterval(() => {
      setIndex((current) => (current + 1) % count);
    }, AUTO_ADVANCE_MS);
    return () => window.clearInterval(id);
  }, [count, paused]);

  useEffect(() => {
    if (index >= count && count > 0) setIndex(0);
  }, [count, index]);

  if (count === 0) return null;

  const featured = sessions[index] ?? sessions[0];
  const summary = featured.summary && isHtmlContent(featured.summary)
    ? stripHtmlToPlainText(featured.summary)
    : featured.summary;

  return (
    <div
      className="relative w-full h-[60vh] md:h-[70vh] bg-brand-navy mb-12 overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {sessions.map((session, i) => (
        <div
          key={session.id}
          className={`absolute inset-0 transition-opacity duration-700 ease-out ${
            i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          aria-hidden={i !== index}
        >
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40"
            style={{ backgroundImage: `url(${session.thumbnail})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/80 to-transparent" />
          </div>
        </div>
      ))}

      <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-12 lg:px-20 max-w-4xl z-10">
        <div className="flex gap-2 mb-6">
          <span className="px-3 py-1 gfi-gradient-bg text-brand-navy rounded-full text-[10px] font-black tracking-widest uppercase">
            Destacado
          </span>
          {featured.level && (
            <span className="px-3 py-1 bg-white/10 text-white rounded-full text-[10px] font-black tracking-widest uppercase border border-white/20">
              {featured.level}
            </span>
          )}
        </div>

        <h1 className="text-4xl md:text-7xl font-black text-white mb-6 leading-[0.95] tracking-tighter uppercase italic">
          {featured.title}
        </h1>

        {summary && (
          <p className="text-slate-300 text-sm md:text-lg mb-8 max-w-2xl font-medium leading-relaxed line-clamp-3">
            {summary}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-6 text-[11px] font-black text-white/60 uppercase tracking-widest mb-10">
          {featured.speaker && (
            <span className="flex items-center gap-2">
              <SafeIcon name="User" className="w-4 h-4 text-brand-cyan" />
              {featured.speaker}
            </span>
          )}
          {featured.duration && (
            <span className="flex items-center gap-2">
              <SafeIcon name="Clock" className="w-4 h-4 text-brand-green" />
              {featured.duration}
            </span>
          )}
        </div>

        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate(`/app/entrenamientos/${featured.id}`)}
            className="flex items-center gap-3 gfi-gradient-bg text-brand-navy px-8 py-4 rounded-full font-black text-xs uppercase tracking-widest transition-all transform hover:scale-105 shadow-2xl"
          >
            <SafeIcon name="Play" className="w-5 h-5 fill-current" />
            Empezar Ahora
          </button>
          <button
            type="button"
            className="flex items-center gap-3 bg-white/5 hover:bg-white/10 text-white px-8 py-4 rounded-full font-black text-xs uppercase tracking-widest transition-colors border border-white/20"
          >
            <SafeIcon name="Plus" className="w-5 h-5" />
            Mi Lista
          </button>
        </div>
      </div>

      {count > 1 && (
        <>
          <button
            type="button"
            aria-label="Sesión anterior"
            onClick={() => goTo(index - 1)}
            className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white border border-white/20 backdrop-blur-sm transition-colors"
          >
            <SafeIcon name="ChevronLeft" className="w-6 h-6" />
          </button>
          <button
            type="button"
            aria-label="Sesión siguiente"
            onClick={() => goTo(index + 1)}
            className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white border border-white/20 backdrop-blur-sm transition-colors"
          >
            <SafeIcon name="ChevronRight" className="w-6 h-6" />
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
            {sessions.map((session, i) => (
              <button
                key={session.id}
                type="button"
                aria-label={`Ir a sesión ${i + 1}`}
                aria-current={i === index ? 'true' : undefined}
                onClick={() => goTo(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === index ? 'w-8 bg-white' : 'w-2 bg-white/40 hover:bg-white/70'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
